import type { DemoWorkspaceMethod } from "./demo-workspace-contract";
import { CyraError } from "./errors";

export const createOutcomeOperations = [
  "PARTNER_CREATE",
  "CONTACT_CREATE",
  "EXPERIENCE_CREATE",
  "OFFERING_CREATE",
  "SCHEDULE_CREATE",
  "SCHEDULE_SERIES_CREATE",
  "CITY_CREATE",
  "DESTINATION_CREATE",
  "LEGAL_DRAFT_CREATE",
  "LEGAL_REVISION_CREATE",
  "INVITATION_CREATE",
] as const;
export type CreateOutcomeOperation = (typeof createOutcomeOperations)[number];
export const updateOutcomeOperations = [
  "PARTNER_UPDATE",
  "PARTNER_LIFECYCLE",
  "CONTACT_UPDATE",
  "CONTACT_LIFECYCLE",
  "EXPERIENCE_UPDATE",
  "EXPERIENCE_LIFECYCLE",
  "OFFERING_UPDATE",
  "OFFERING_LIFECYCLE",
  "SCHEDULE_UPDATE",
  "SCHEDULE_SERIES_UPDATE",
  "SCHEDULE_STATUS",
  "CITY_UPDATE",
  "DESTINATION_UPDATE",
  "DESTINATION_LIFECYCLE",
  "LEGAL_DRAFT_UPDATE",
  "LEGAL_PUBLISH",
] as const;
export type UpdateOutcomeOperation = (typeof updateOutcomeOperations)[number];
export const governedOutcomeOperations = [
  "CONTENT_DRAFT_CREATE",
  "CONTENT_DRAFT_UPDATE",
  "CONTENT_PUBLISH",
  "CONTENT_UNPUBLISH",
  "ACTIVITY_CREATE",
  "INVITATION_RESEND",
  "INVITATION_CANCEL",
  "USER_SUSPEND",
  "USER_RESTORE",
  "USER_BAN",
  "USER_SESSIONS_REVOKE",
  "USER_ROLES_REPLACE",
  "USER_MFA_RESET",
  "NOTIFICATION_READ",
  "APPROVAL_ACTION_POLICY_UPDATE",
  "APPROVAL_EXECUTION_CONTROL_UPDATE",
  "SENSITIVE_PROJECTION_CONTROL_UPDATE",
] as const;
export type GovernedOutcomeOperation = (typeof governedOutcomeOperations)[number];
export const approvalWorkflowOutcomeOperations = [
  "APPROVAL_WITHDRAW",
  "APPROVAL_REVISE",
  "APPROVAL_APPROVE",
  "APPROVAL_REJECT",
  "APPROVAL_REQUEST_CHANGES",
  "APPROVAL_APPLY",
  "APPROVAL_RETRY",
  "APPROVAL_EXECUTION_RELEASE",
] as const;
export type ApprovalWorkflowOutcomeOperation = (typeof approvalWorkflowOutcomeOperations)[number];
export const partnerImportOutcomeOperations = [
  "PARTNER_SETUP_CREATE",
  "PARTNER_SETUP_SAVE",
  "PARTNER_IMPORT_PREVIEW",
  "PARTNER_IMPORT_STAGE",
  "PARTNER_IMPORT_REGENERATE",
  "PARTNER_IMPORT_DECISION",
  "PARTNER_IMPORT_APPLY",
  "PARTNER_IMPORT_CANCEL",
  "PARTNER_IMPORT_RETRY",
  "PARTNER_IMPORT_PURGE_RETRY",
  "PARTNER_IMPORT_EVIDENCE_HOLD",
] as const;
export type PartnerImportOutcomeOperation = (typeof partnerImportOutcomeOperations)[number];
export type ReconcilableOutcomeOperation =
  | CreateOutcomeOperation
  | UpdateOutcomeOperation
  | GovernedOutcomeOperation
  | ApprovalWorkflowOutcomeOperation
  | PartnerImportOutcomeOperation;
export const governedApprovalOutcomeOperations = [
  "INVITATION_RESEND",
  "INVITATION_CANCEL",
  "USER_SUSPEND",
  "USER_BAN",
  "USER_RESTORE",
  "USER_SESSIONS_REVOKE",
  "USER_MFA_RESET",
  "USER_ROLES_REPLACE",
] as const;
export type GovernedApprovalOutcomeOperation = (typeof governedApprovalOutcomeOperations)[number];
export type DirectUpdateOutcomeOperation =
  | Exclude<UpdateOutcomeOperation, "SCHEDULE_STATUS">
  | "SCHEDULE_CANCEL"
  | "SCHEDULE_ARCHIVE"
  | GovernedOutcomeOperation
  | ApprovalWorkflowOutcomeOperation
  | PartnerImportOutcomeOperation
  | "ADMINISTRATOR_SUSPEND"
  | "ADMINISTRATOR_RESTORE";
export type OutcomeOperation =
  | CreateOutcomeOperation
  | DirectUpdateOutcomeOperation
  | `${CreateOutcomeOperation | UpdateOutcomeOperation | GovernedApprovalOutcomeOperation | "PARTNER_IMPORT_APPLY"}_APPROVAL`;
export const outcomeOperations: readonly OutcomeOperation[] = [
  ...createOutcomeOperations.flatMap((operation) => [operation, `${operation}_APPROVAL` as const]),
  ...updateOutcomeOperations.filter((operation) => operation !== "SCHEDULE_STATUS"),
  "SCHEDULE_CANCEL",
  "SCHEDULE_ARCHIVE",
  ...updateOutcomeOperations.map((operation) => `${operation}_APPROVAL` as const),
  ...governedOutcomeOperations,
  ...approvalWorkflowOutcomeOperations,
  ...partnerImportOutcomeOperations,
  "ADMINISTRATOR_SUSPEND",
  "ADMINISTRATOR_RESTORE",
  ...governedApprovalOutcomeOperations.map((operation) => `${operation}_APPROVAL` as const),
  "PARTNER_IMPORT_APPLY_APPROVAL",
];
type Definition = {
  path: string;
  demoMethod?: DemoWorkspaceMethod | undefined;
  targetField?: "parentId" | "targetId" | undefined;
  moduleCode: string;
  actionCode: string;
  entityType: string;
};
const catalogue = "PARTNER_CATALOG_MUTATION",
  reference = "DESTINATION_REFERENCE_MUTATION";
export const createOutcomeDefinitions: Readonly<
  Record<CreateOutcomeOperation, Definition & { demoMethod: DemoWorkspaceMethod }>
> = {
  PARTNER_CREATE: {
    path: "/v1/admin/partners",
    demoMethod: "createPartner",
    moduleCode: "PARTNERS",
    actionCode: catalogue,
    entityType: "PARTNER",
  },
  CONTACT_CREATE: {
    path: "/v1/admin/partners/:target/contacts",
    demoMethod: "createPartnerContact",
    targetField: "parentId",
    moduleCode: "PARTNER_CONTACTS",
    actionCode: catalogue,
    entityType: "PARTNER_CONTACT",
  },
  EXPERIENCE_CREATE: {
    path: "/v1/admin/experiences",
    demoMethod: "createExperience",
    moduleCode: "EXPERIENCES",
    actionCode: catalogue,
    entityType: "EXPERIENCE",
  },
  OFFERING_CREATE: {
    path: "/v1/admin/partners/:target/offerings",
    demoMethod: "createPartnerOffering",
    targetField: "parentId",
    moduleCode: "PARTNER_OFFERINGS",
    actionCode: catalogue,
    entityType: "PARTNER_OFFERING",
  },
  SCHEDULE_CREATE: {
    path: "/v1/admin/partner-offerings/:target/schedules",
    demoMethod: "createPartnerSchedule",
    targetField: "parentId",
    moduleCode: "PARTNER_SCHEDULES",
    actionCode: catalogue,
    entityType: "PARTNER_SCHEDULE",
  },
  SCHEDULE_SERIES_CREATE: {
    path: "/v1/admin/partner-offerings/:target/schedule-series",
    demoMethod: "createScheduleSeries",
    targetField: "parentId",
    moduleCode: "PARTNER_SCHEDULES",
    actionCode: "PARTNER_SCHEDULE_SERIES_MUTATION",
    entityType: "PARTNER_SCHEDULE_SERIES",
  },
  CITY_CREATE: {
    path: "/v1/admin/canonical-cities",
    demoMethod: "createCanonicalCity",
    moduleCode: "DESTINATIONS",
    actionCode: reference,
    entityType: "CANONICAL_CITY",
  },
  DESTINATION_CREATE: {
    path: "/v1/admin/destinations",
    demoMethod: "createDestinationReference",
    moduleCode: "DESTINATIONS",
    actionCode: reference,
    entityType: "DESTINATION",
  },
  LEGAL_DRAFT_CREATE: {
    path: "/v1/admin/legal-document-drafts",
    demoMethod: "saveLegalDraft",
    moduleCode: "LEGAL",
    actionCode: "LEGAL_MUTATION",
    entityType: "LEGAL_DOCUMENT",
  },
  LEGAL_REVISION_CREATE: {
    path: "/v1/admin/legal-document-versions/:target/draft-revision",
    demoMethod: "createLegalRevision",
    targetField: "targetId",
    moduleCode: "LEGAL",
    actionCode: "LEGAL_MUTATION",
    entityType: "LEGAL_DOCUMENT",
  },
  INVITATION_CREATE: {
    path: "/v1/admin/user-invitations",
    demoMethod: "inviteUser",
    moduleCode: "USER_MODERATION",
    actionCode: "INVITATION_GOVERNANCE_MUTATION",
    entityType: "USER_INVITATION",
  },
};
export const updateMutationDefinitions = {
  PARTNER_UPDATE: {
    ...createOutcomeDefinitions.PARTNER_CREATE,
    path: "/v1/admin/partners/:target",
    demoMethod: "updatePartner",
    method: "PATCH",
    targetField: "targetId",
  },
  PARTNER_LIFECYCLE: {
    ...createOutcomeDefinitions.PARTNER_CREATE,
    path: "/v1/admin/partners/:target/lifecycle",
    demoMethod: "updatePartnerLifecycle",
    method: "POST",
    targetField: "targetId",
  },
  CONTACT_UPDATE: {
    ...createOutcomeDefinitions.CONTACT_CREATE,
    path: "/v1/admin/partner-contacts/:target",
    demoMethod: "updatePartnerContact",
    method: "PATCH",
    targetField: "targetId",
  },
  CONTACT_LIFECYCLE: {
    ...createOutcomeDefinitions.CONTACT_CREATE,
    path: "/v1/admin/partner-contacts/:target/lifecycle",
    demoMethod: "updatePartnerContactLifecycle",
    method: "POST",
    targetField: "targetId",
  },
  EXPERIENCE_UPDATE: {
    ...createOutcomeDefinitions.EXPERIENCE_CREATE,
    path: "/v1/admin/experiences/:target",
    demoMethod: "updateExperience",
    method: "PATCH",
    targetField: "targetId",
  },
  EXPERIENCE_LIFECYCLE: {
    ...createOutcomeDefinitions.EXPERIENCE_CREATE,
    path: "/v1/admin/experiences/:target/lifecycle",
    demoMethod: "updateExperienceLifecycle",
    method: "POST",
    targetField: "targetId",
  },
  OFFERING_UPDATE: {
    ...createOutcomeDefinitions.OFFERING_CREATE,
    path: "/v1/admin/partner-offerings/:target",
    demoMethod: "updatePartnerOffering",
    method: "PATCH",
    targetField: "targetId",
  },
  OFFERING_LIFECYCLE: {
    ...createOutcomeDefinitions.OFFERING_CREATE,
    path: "/v1/admin/partner-offerings/:target/lifecycle",
    demoMethod: "updatePartnerOfferingLifecycle",
    method: "POST",
    targetField: "targetId",
  },
  SCHEDULE_UPDATE: {
    ...createOutcomeDefinitions.SCHEDULE_CREATE,
    path: "/v1/admin/partner-schedules/:target",
    demoMethod: "updatePartnerSchedule",
    method: "PATCH",
    targetField: "targetId",
  },
  SCHEDULE_SERIES_UPDATE: {
    ...createOutcomeDefinitions.SCHEDULE_SERIES_CREATE,
    path: "/v1/admin/schedule-series/:target",
    demoMethod: "updateScheduleSeries",
    method: "PATCH",
    targetField: "targetId",
  },
  SCHEDULE_STATUS: {
    ...createOutcomeDefinitions.SCHEDULE_CREATE,
    path: "/v1/admin/partner-schedules/:target/cancel",
    demoMethod: "updatePartnerScheduleStatus",
    method: "POST",
    targetField: "targetId",
  },
  CITY_UPDATE: {
    ...createOutcomeDefinitions.CITY_CREATE,
    path: "/v1/admin/canonical-cities/:target",
    demoMethod: "updateCanonicalCity",
    method: "PATCH",
    targetField: "targetId",
  },
  DESTINATION_UPDATE: {
    ...createOutcomeDefinitions.DESTINATION_CREATE,
    path: "/v1/admin/destinations/:target",
    demoMethod: "updateDestinationReference",
    method: "PATCH",
    targetField: "targetId",
  },
  DESTINATION_LIFECYCLE: {
    ...createOutcomeDefinitions.DESTINATION_CREATE,
    path: "/v1/admin/destinations/:target/lifecycle",
    demoMethod: "updateDestinationLifecycle",
    method: "POST",
    targetField: "targetId",
  },
  LEGAL_DRAFT_UPDATE: {
    ...createOutcomeDefinitions.LEGAL_DRAFT_CREATE,
    path: "/v1/admin/legal-document-drafts/:target",
    demoMethod: "saveLegalDraft",
    method: "PATCH",
    targetField: "targetId",
  },
  LEGAL_PUBLISH: {
    ...createOutcomeDefinitions.LEGAL_DRAFT_CREATE,
    path: "/v1/admin/legal-document-drafts/:target/publish",
    demoMethod: "publishLegal",
    method: "POST",
    targetField: "targetId",
  },
} as const satisfies Record<
  UpdateOutcomeOperation,
  Definition & { method: "POST" | "PATCH"; targetField: "targetId" }
>;
export const governedMutationDefinitions = {
  CONTENT_DRAFT_CREATE: {
    demoMethod: undefined,
    path: "/v1/admin/public-content/:target/drafts",
    method: "POST",
    targetField: "targetId",
    moduleCode: "LEGAL",
    actionCode: "LEGAL_GOVERNANCE_MUTATION",
    entityType: "PUBLIC_CONTENT",
  },
  CONTENT_DRAFT_UPDATE: {
    demoMethod: undefined,
    path: "/v1/admin/public-content/versions/:target",
    method: "PATCH",
    targetField: "targetId",
    moduleCode: "LEGAL",
    actionCode: "LEGAL_GOVERNANCE_MUTATION",
    entityType: "PUBLIC_CONTENT",
  },
  CONTENT_PUBLISH: {
    demoMethod: undefined,
    path: "/v1/admin/public-content/versions/:target/publish",
    method: "POST",
    targetField: "targetId",
    moduleCode: "LEGAL",
    actionCode: "LEGAL_GOVERNANCE_MUTATION",
    entityType: "PUBLIC_CONTENT",
  },
  CONTENT_UNPUBLISH: {
    demoMethod: undefined,
    path: "/v1/admin/public-content/versions/:target/unpublish",
    method: "POST",
    targetField: "targetId",
    moduleCode: "LEGAL",
    actionCode: "LEGAL_GOVERNANCE_MUTATION",
    entityType: "PUBLIC_CONTENT",
  },

  ACTIVITY_CREATE: {
    demoMethod: undefined,
    path: "/v1/admin/destinations/:target/activities",
    method: "POST",
    targetField: "targetId",
    moduleCode: "PARTNER_SCHEDULES",
    actionCode: "PARTNER_CATALOG_MUTATION",
    entityType: "PARTNER_SCHEDULE",
  },
  INVITATION_RESEND: {
    path: "/v1/admin/user-invitations/:target/resend",
    demoMethod: "resendInvitation",
    method: "POST",
    targetField: "targetId",
    moduleCode: "USER_MODERATION",
    actionCode: "INVITATION_GOVERNANCE_MUTATION",
    entityType: "USER_INVITATION",
  },
  INVITATION_CANCEL: {
    path: "/v1/admin/user-invitations/:target/cancel",
    demoMethod: "cancelInvitation",
    method: "POST",
    targetField: "targetId",
    moduleCode: "USER_MODERATION",
    actionCode: "INVITATION_GOVERNANCE_MUTATION",
    entityType: "USER_INVITATION",
  },
  USER_SUSPEND: {
    path: "/v1/admin/users/:target/suspend",
    demoMethod: "mutateAccess",
    method: "POST",
    targetField: "targetId",
    moduleCode: "USER_MODERATION",
    actionCode: "USER_GOVERNANCE_MUTATION",
    entityType: "USER",
  },
  USER_RESTORE: {
    path: "/v1/admin/users/:target/restore",
    demoMethod: "mutateAccess",
    method: "POST",
    targetField: "targetId",
    moduleCode: "USER_MODERATION",
    actionCode: "USER_GOVERNANCE_MUTATION",
    entityType: "USER",
  },
  USER_BAN: {
    path: "/v1/admin/users/:target/ban",
    demoMethod: "mutateAccess",
    method: "POST",
    targetField: "targetId",
    moduleCode: "USER_MODERATION",
    actionCode: "USER_GOVERNANCE_MUTATION",
    entityType: "USER",
  },
  USER_SESSIONS_REVOKE: {
    path: "/v1/admin/users/:target/sessions/revoke",
    demoMethod: "revokeSessions",
    method: "POST",
    targetField: "targetId",
    moduleCode: "USER_MODERATION",
    actionCode: "USER_GOVERNANCE_MUTATION",
    entityType: "USER",
  },
  USER_ROLES_REPLACE: {
    path: "/v1/admin/users/:target/roles",
    demoMethod: "updateRoles",
    method: "PUT",
    targetField: "targetId",
    moduleCode: "USER_MODERATION",
    actionCode: "USER_GOVERNANCE_MUTATION",
    entityType: "USER",
  },
  USER_MFA_RESET: {
    path: "/v1/admin/administrators/:target/reset-mfa",
    demoMethod: "resetAdministratorMfa",
    method: "POST",
    targetField: "targetId",
    moduleCode: "USER_MODERATION",
    actionCode: "USER_GOVERNANCE_MUTATION",
    entityType: "USER",
  },
  NOTIFICATION_READ: {
    path: "/v1/admin/notifications/:target/read",
    demoMethod: "markNotificationRead",
    method: "POST",
    targetField: "targetId",
    moduleCode: "APPROVALS",
    actionCode: "NOTIFICATION_READ",
    entityType: "NOTIFICATION",
  },
  APPROVAL_ACTION_POLICY_UPDATE: {
    path: "/v1/admin/approval-action-policies/:target",
    demoMethod: "updateActionPolicy",
    method: "PATCH",
    targetField: "targetId",
    moduleCode: "APPROVALS",
    actionCode: "APPROVAL_ACTION_POLICY_UPDATE",
    entityType: "APPROVAL_POLICY",
  },
  APPROVAL_EXECUTION_CONTROL_UPDATE: {
    path: "/v1/admin/approval-execution-controls/:target",
    demoMethod: "updateExecutionControl",
    method: "PATCH",
    targetField: "targetId",
    moduleCode: "APPROVALS",
    actionCode: "APPROVAL_EXECUTION_CONTROL_UPDATE",
    entityType: "APPROVAL_EXECUTION_CONTROL",
  },
  SENSITIVE_PROJECTION_CONTROL_UPDATE: {
    path: "/v1/admin/sensitive-projection-controls/:target",
    demoMethod: "updateSensitiveReadControl",
    method: "PATCH",
    targetField: "targetId",
    moduleCode: "APPROVALS",
    actionCode: "SENSITIVE_PROJECTION_CONTROL_UPDATE",
    entityType: "SENSITIVE_PROJECTION_CONTROL",
  },
} as const satisfies Record<
  GovernedOutcomeOperation,
  Definition & { method: "POST" | "PUT" | "PATCH"; targetField: "targetId" }
>;
export const approvalWorkflowMutationDefinitions = {
  APPROVAL_WITHDRAW: {
    path: "/v1/admin/approval-requests/:target/withdraw",
    demoMethod: "withdrawApprovalRequest",
    method: "POST",
    targetField: "targetId",
    moduleCode: "APPROVALS",
    actionCode: "APPROVAL_WITHDRAW",
    entityType: "APPROVAL_REQUEST",
  },
  APPROVAL_REVISE: {
    path: "/v1/admin/approval-requests/:target/revise",
    demoMethod: "reviseApprovalRequest",
    method: "POST",
    targetField: "targetId",
    moduleCode: "APPROVALS",
    actionCode: "APPROVAL_REVISE",
    entityType: "APPROVAL_REQUEST",
  },
  APPROVAL_APPROVE: {
    path: "/v1/admin/approval-requests/:target/approve",
    demoMethod: "decideApprovalRequest",
    method: "POST",
    targetField: "targetId",
    moduleCode: "APPROVALS",
    actionCode: "APPROVAL_APPROVE",
    entityType: "APPROVAL_REQUEST",
  },
  APPROVAL_REJECT: {
    path: "/v1/admin/approval-requests/:target/reject",
    demoMethod: "decideApprovalRequest",
    method: "POST",
    targetField: "targetId",
    moduleCode: "APPROVALS",
    actionCode: "APPROVAL_REJECT",
    entityType: "APPROVAL_REQUEST",
  },
  APPROVAL_REQUEST_CHANGES: {
    path: "/v1/admin/approval-requests/:target/request-changes",
    demoMethod: "decideApprovalRequest",
    method: "POST",
    targetField: "targetId",
    moduleCode: "APPROVALS",
    actionCode: "APPROVAL_REQUEST_CHANGES",
    entityType: "APPROVAL_REQUEST",
  },
  APPROVAL_APPLY: {
    path: "/v1/admin/approval-requests/:target/apply",
    demoMethod: "applyApprovalRequest",
    method: "POST",
    targetField: "targetId",
    moduleCode: "APPROVALS",
    actionCode: "APPROVAL_APPLY",
    entityType: "APPROVAL_REQUEST",
  },
  APPROVAL_RETRY: {
    path: "/v1/admin/approval-requests/:target/retry",
    demoMethod: "retryApprovalRequest",
    method: "POST",
    targetField: "targetId",
    moduleCode: "APPROVALS",
    actionCode: "APPROVAL_RETRY",
    entityType: "APPROVAL_REQUEST",
  },
  APPROVAL_EXECUTION_RELEASE: {
    path: "/v1/admin/approval-requests/:target/execution-release",
    demoMethod: "releaseApprovalExecution",
    method: "POST",
    targetField: "targetId",
    moduleCode: "APPROVALS",
    actionCode: "APPROVAL_EXECUTION_RELEASE",
    entityType: "APPROVAL_REQUEST",
  },
} as const satisfies Record<
  ApprovalWorkflowOutcomeOperation,
  Definition & { method: "POST"; targetField: "targetId" }
>;
export const partnerImportMutationDefinitions = {
  PARTNER_SETUP_CREATE: {
    path: "/v1/admin/partner-imports/setups",
    demoMethod: "createPartnerSetup",
    method: "POST",
    targetField: undefined,
    moduleCode: "PARTNER_IMPORT",
    actionCode: "PARTNER_SETUP_CREATE",
    entityType: "PARTNER_IMPORT",
  },
  PARTNER_SETUP_SAVE: {
    path: "/v1/admin/partner-imports/setups/:target/save",
    demoMethod: "savePartnerSetup",
    method: "POST",
    targetField: "targetId",
    moduleCode: "PARTNER_IMPORT",
    actionCode: "PARTNER_SETUP_SAVE",
    entityType: "PARTNER_IMPORT",
  },
  PARTNER_IMPORT_PREVIEW: {
    path: "/v1/admin/partner-imports/preview",
    demoMethod: "createPartnerImportPreview",
    method: "POST",
    targetField: undefined,
    moduleCode: "PARTNER_IMPORT",
    actionCode: "PARTNER_IMPORT_PREVIEW",
    entityType: "PARTNER_IMPORT",
  },
  PARTNER_IMPORT_STAGE: {
    path: "/v1/admin/partner-imports/batches/:target/stage",
    demoMethod: "stagePartnerImport",
    method: "POST",
    targetField: "targetId",
    moduleCode: "PARTNER_IMPORT",
    actionCode: "PARTNER_IMPORT_STAGE",
    entityType: "PARTNER_IMPORT",
  },
  PARTNER_IMPORT_REGENERATE: {
    path: "/v1/admin/partner-imports/batches/:target/regenerate",
    demoMethod: "regeneratePartnerImport",
    method: "POST",
    targetField: "targetId",
    moduleCode: "PARTNER_IMPORT",
    actionCode: "PARTNER_IMPORT_REGENERATE",
    entityType: "PARTNER_IMPORT",
  },
  PARTNER_IMPORT_DECISION: {
    path: "/v1/admin/partner-imports/changes/:target/decision",
    demoMethod: "updatePartnerImportDecision",
    method: "PATCH",
    targetField: "targetId",
    moduleCode: "PARTNER_IMPORT",
    actionCode: "PARTNER_IMPORT_DECISION",
    entityType: "PARTNER_IMPORT_CHANGE",
  },
  PARTNER_IMPORT_APPLY: {
    path: "/v1/admin/partner-imports/batches/:target/apply",
    demoMethod: "applyPartnerImport",
    method: "POST",
    targetField: "targetId",
    moduleCode: "PARTNER_IMPORT",
    actionCode: "PARTNER_IMPORT_APPLY",
    entityType: "PARTNER_IMPORT",
  },
  PARTNER_IMPORT_CANCEL: {
    path: "/v1/admin/partner-imports/batches/:target/cancel",
    demoMethod: "controlPartnerImport",
    method: "POST",
    targetField: "targetId",
    moduleCode: "PARTNER_IMPORT",
    actionCode: "PARTNER_IMPORT_CANCEL",
    entityType: "PARTNER_IMPORT",
  },
  PARTNER_IMPORT_RETRY: {
    path: "/v1/admin/partner-imports/batches/:target/retry",
    demoMethod: "controlPartnerImport",
    method: "POST",
    targetField: "targetId",
    moduleCode: "PARTNER_IMPORT",
    actionCode: "PARTNER_IMPORT_RETRY",
    entityType: "PARTNER_IMPORT",
  },
  PARTNER_IMPORT_PURGE_RETRY: {
    path: "/v1/admin/partner-imports/batches/:target/retention/purge-retry",
    method: "POST",
    demoMethod: undefined,
    targetField: "targetId",
    moduleCode: "PARTNER_IMPORT",
    actionCode: "PARTNER_IMPORT_PURGE_RETRY",
    entityType: "PARTNER_IMPORT_RETENTION",
  },
  PARTNER_IMPORT_EVIDENCE_HOLD: {
    path: "/v1/admin/partner-imports/batches/:target/retention/hold",
    method: "POST",
    demoMethod: undefined,
    targetField: "targetId",
    moduleCode: "PARTNER_IMPORT",
    actionCode: "PARTNER_IMPORT_EVIDENCE_HOLD",
    entityType: "PARTNER_IMPORT_RETENTION",
  },
} as const satisfies Record<
  PartnerImportOutcomeOperation,
  Definition & { method: "POST" | "PATCH" }
>;
export const existingOutcomeOperations = [
  ...updateOutcomeOperations,
  ...governedOutcomeOperations,
  ...approvalWorkflowOutcomeOperations,
  ...partnerImportOutcomeOperations,
] as const;
export type ExistingOutcomeOperation = (typeof existingOutcomeOperations)[number];
export const existingMutationDefinitions = {
  ...updateMutationDefinitions,
  ...governedMutationDefinitions,
  ...approvalWorkflowMutationDefinitions,
  ...partnerImportMutationDefinitions,
} as const satisfies Record<
  ExistingOutcomeOperation,
  Definition & { method: "POST" | "PUT" | "PATCH" }
>;
export type CreateOutcomeBinding = { operation: CreateOutcomeOperation; targetId?: string };
export type UpdateOutcomeBinding = {
  operation: ExistingOutcomeOperation;
  targetId?: string;
  sourceOperation?: DirectUpdateOutcomeOperation;
};
export type UpdateProposalOutcomeBinding = {
  operation: UpdateOutcomeOperation | GovernedApprovalOutcomeOperation | "PARTNER_IMPORT_APPLY";
  targetId: string;
};
function unavailable(): never {
  throw new CyraError({
    code: "MUTATION_RECONCILIATION_UNAVAILABLE",
    requestId: "client",
    message: "This mutation has no exact registered outcome owner and target.",
  });
}
function definition(operation: CreateOutcomeOperation): Definition {
  if (!createOutcomeOperations.includes(operation)) return unavailable();
  return createOutcomeDefinitions[operation];
}
// IDs are opaque path segments; retained legal and imported content IDs contain dots.
// A leading identifier character excludes dot-segments; slashes, escapes and whitespace remain invalid.
function target(value: unknown): string {
  if (typeof value !== "string" || !/^[A-Za-z0-9_-][A-Za-z0-9_.-]{0,63}$/u.test(value))
    return unavailable();
  return value;
}
function compositeTarget(value: unknown): string {
  if (typeof value !== "string" || !/^[A-Za-z0-9_-]{1,64}~[A-Za-z0-9_-]{1,64}$/u.test(value))
    return unavailable();
  return value;
}
export function createMutationPath(operation: CreateOutcomeOperation, targetId?: string): string {
  const owner = definition(operation);
  if (owner.targetField) return owner.path.replace(":target", encodeURIComponent(target(targetId)));
  if (targetId !== undefined && targetId !== "") return unavailable();
  return owner.path;
}
/** Classify only the registered owning routes. Draft values never form operation identity. */
export function directCreateBinding(
  request: Pick<Request, "method" | "url">,
): CreateOutcomeBinding | undefined {
  if (request.method !== "POST") return undefined;
  const path = new URL(request.url).pathname;
  for (const operation of createOutcomeOperations) {
    const owner = definition(operation);
    const match = new RegExp(`^${owner.path.replace(":target", "([^/]+)")}$`, "u").exec(path);
    if (!match) continue;
    return {
      operation,
      ...(owner.targetField ? { targetId: target(decodeURIComponent(match[1] ?? "")) } : {}),
    };
  }
  return undefined;
}
/** A known typed create must not fall through to a new approval-route intent on malformed metadata. */
export function createProposalBinding(value: unknown): CreateOutcomeBinding | undefined {
  if (
    !value ||
    typeof value !== "object" ||
    !("payload" in value) ||
    !value.payload ||
    typeof value.payload !== "object" ||
    !("operation" in value.payload)
  )
    return undefined;
  const operation = value.payload.operation;
  if (!createOutcomeOperations.includes(operation as CreateOutcomeOperation)) return undefined;
  const owner = definition(operation as CreateOutcomeOperation);
  if (
    !("actionCode" in value) ||
    value.actionCode !== owner.actionCode ||
    !("moduleCode" in value) ||
    value.moduleCode !== owner.moduleCode ||
    !("entityType" in value) ||
    value.entityType !== owner.entityType
  )
    return unavailable();
  const payload = value.payload as Record<string, unknown>;
  const targetId = owner.targetField ? target(payload[owner.targetField]) : undefined;
  if (targetId && (!("entityId" in value) || value.entityId !== targetId)) return unavailable();
  return { operation: operation as CreateOutcomeOperation, ...(targetId ? { targetId } : {}) };
}
export function demoCreateBinding(
  method: DemoWorkspaceMethod,
  args: unknown[],
): CreateOutcomeBinding | undefined {
  if (method === "createApprovalRequest") return createProposalBinding(args[0]);
  if (method === "saveLegalDraft" && args[1] !== undefined) return undefined;
  const operation = createOutcomeOperations.find(
    (value) => definition(value).demoMethod === method,
  );
  if (!operation) return undefined;
  return {
    operation,
    ...(definition(operation).targetField ? { targetId: target(args[0]) } : {}),
  };
}

export function updateMutationPath(
  operation: ExistingOutcomeOperation,
  targetId?: string,
  sourceOperation?: DirectUpdateOutcomeOperation,
): string {
  if (!Object.hasOwn(existingMutationDefinitions, operation)) return unavailable();
  const owner = existingMutationDefinitions[operation];
  if (
    sourceOperation &&
    (operation === "SCHEDULE_STATUS"
      ? sourceOperation !== "SCHEDULE_CANCEL" && sourceOperation !== "SCHEDULE_ARCHIVE"
      : operation === "USER_SUSPEND"
        ? sourceOperation !== "USER_SUSPEND" && sourceOperation !== "ADMINISTRATOR_SUSPEND"
        : operation === "USER_RESTORE"
          ? sourceOperation !== "USER_RESTORE" && sourceOperation !== "ADMINISTRATOR_RESTORE"
          : sourceOperation !== operation)
  )
    return unavailable();
  let path =
    operation === "SCHEDULE_STATUS" && sourceOperation === "SCHEDULE_ARCHIVE"
      ? "/v1/admin/partner-schedules/:target/archive"
      : operation === "USER_SUSPEND" && sourceOperation === "ADMINISTRATOR_SUSPEND"
        ? "/v1/admin/administrators/:target/suspend"
        : operation === "USER_RESTORE" && sourceOperation === "ADMINISTRATOR_RESTORE"
          ? "/v1/admin/administrators/:target/restore"
          : owner.path;
  if (operation === "APPROVAL_ACTION_POLICY_UPDATE") {
    const [actionCode, roleCode, extra] = compositeTarget(targetId).split("~");
    if (!actionCode || !roleCode || extra !== undefined) return unavailable();
    path = `/v1/admin/approval-action-policies/${encodeURIComponent(actionCode)}/${encodeURIComponent(roleCode)}`;
    return path;
  }
  if (operation === "PARTNER_IMPORT_PURGE_RETRY") {
    const [batchId, retentionClass, extra] = purgeTarget(targetId).split("~");
    if (!batchId || !retentionClass || extra !== undefined) return unavailable();
    return path.replace(":target", encodeURIComponent(batchId));
  }
  if (!owner.targetField) {
    if (targetId !== undefined && targetId !== "") return unavailable();
    return path;
  }
  return path.replace(":target", encodeURIComponent(target(targetId)));
}

function purgeTarget(value: unknown): string {
  if (
    typeof value !== "string" ||
    !/^[A-Za-z0-9_-]{1,64}~(?:RAW_SOURCE|NORMALIZED_PRIVATE|TYPED_RESULTS)$/u.test(value)
  )
    return unavailable();
  return value;
}

/** Two Schedule routes share one unresolved form boundary but retain their exact original source. */
export function directUpdateBinding(
  request: Pick<Request, "method" | "url">,
): UpdateOutcomeBinding | undefined {
  const path = new URL(request.url).pathname;
  for (const operation of existingOutcomeOperations) {
    const owner = existingMutationDefinitions[operation];
    if (request.method !== owner.method) continue;
    if (operation === "APPROVAL_ACTION_POLICY_UPDATE") {
      const match = /^\/v1\/admin\/approval-action-policies\/([^/]+)\/([^/]+)$/u.exec(path);
      if (match)
        return {
          operation,
          sourceOperation: operation,
          targetId: compositeTarget(
            `${decodeURIComponent(match[1] ?? "")}~${decodeURIComponent(match[2] ?? "")}`,
          ),
        };
      continue;
    }
    // The purge owner is batch + retention class. The class is in the body, so a URL-only
    // classifier must not collapse all three independently retryable commands into one scope.
    if (operation === "PARTNER_IMPORT_PURGE_RETRY") continue;
    const variants =
      operation === "SCHEDULE_STATUS"
        ? ([
            ["SCHEDULE_CANCEL", owner.path],
            ["SCHEDULE_ARCHIVE", "/v1/admin/partner-schedules/:target/archive"],
          ] as const)
        : operation === "USER_SUSPEND"
          ? ([
              ["USER_SUSPEND", owner.path],
              ["ADMINISTRATOR_SUSPEND", "/v1/admin/administrators/:target/suspend"],
            ] as const)
          : operation === "USER_RESTORE"
            ? ([
                ["USER_RESTORE", owner.path],
                ["ADMINISTRATOR_RESTORE", "/v1/admin/administrators/:target/restore"],
              ] as const)
            : ([[operation, owner.path]] as const);
    for (const [sourceOperation, source] of variants) {
      const match = new RegExp(`^${source.replace(":target", "([^/]+)")}$`, "u").exec(path);
      if (!match) continue;
      if (!owner.targetField) return { operation, sourceOperation };
      return { operation, sourceOperation, targetId: target(decodeURIComponent(match[1] ?? "")) };
    }
  }
  return undefined;
}

/** Bind the one registered command whose exact owner is split across URL and JSON body. */
export function bodyBoundUpdateBinding(
  request: Pick<Request, "method" | "url">,
  value: unknown,
): UpdateOutcomeBinding | undefined {
  const owner = partnerImportMutationDefinitions.PARTNER_IMPORT_PURGE_RETRY;
  if (request.method !== owner.method) return undefined;
  const match = new RegExp(`^${owner.path.replace(":target", "([^/]+)")}$`, "u").exec(
    new URL(request.url).pathname,
  );
  if (!match) return undefined;
  if (!value || typeof value !== "object" || !("retentionClass" in value)) return unavailable();
  const batchId = target(decodeURIComponent(match[1] ?? ""));
  return {
    operation: "PARTNER_IMPORT_PURGE_RETRY",
    sourceOperation: "PARTNER_IMPORT_PURGE_RETRY",
    targetId: purgeTarget(`${batchId}~${String(value.retentionClass)}`),
  };
}

export function updateProposalBinding(value: unknown): UpdateProposalOutcomeBinding | undefined {
  if (!value || typeof value !== "object") return undefined;
  if ("actionCode" in value && value.actionCode === "PARTNER_IMPORT_APPLY") {
    if (
      !("payload" in value) ||
      !value.payload ||
      typeof value.payload !== "object" ||
      !("batchId" in value.payload) ||
      !("moduleCode" in value) ||
      value.moduleCode !== "PARTNER_IMPORT" ||
      !("entityType" in value) ||
      value.entityType !== "PARTNER_IMPORT"
    )
      return unavailable();
    const targetId = target(value.payload.batchId);
    if (!("entityId" in value) || value.entityId !== targetId) return unavailable();
    return { operation: "PARTNER_IMPORT_APPLY", targetId };
  }
  if (
    !("payload" in value) ||
    !value.payload ||
    typeof value.payload !== "object" ||
    !("operation" in value.payload)
  )
    return undefined;
  const operation = value.payload.operation;
  const proposalOperations = [
    ...updateOutcomeOperations,
    ...governedApprovalOutcomeOperations,
  ] as readonly ExistingOutcomeOperation[];
  if (!proposalOperations.includes(operation as ExistingOutcomeOperation)) return undefined;
  const owner = existingMutationDefinitions[operation as ExistingOutcomeOperation];
  if (
    !("actionCode" in value) ||
    value.actionCode !== owner.actionCode ||
    !("moduleCode" in value) ||
    value.moduleCode !== owner.moduleCode ||
    !("entityType" in value) ||
    value.entityType !== owner.entityType
  )
    return unavailable();
  const targetId =
    operation === "APPROVAL_ACTION_POLICY_UPDATE"
      ? compositeTarget((value.payload as Record<string, unknown>)["targetId"])
      : target((value.payload as Record<string, unknown>)["targetId"]);
  if (!("entityId" in value) || value.entityId !== targetId) return unavailable();
  return {
    operation: operation as UpdateOutcomeOperation | GovernedApprovalOutcomeOperation,
    targetId,
  };
}

export function demoUpdateOperationArgs(
  operation: ExistingOutcomeOperation,
  targetId?: string,
): unknown[] {
  if (operation === "PARTNER_IMPORT_PREVIEW" || operation === "PARTNER_SETUP_CREATE") return [];
  if (!targetId) return unavailable();
  if (operation === "LEGAL_DRAFT_UPDATE") return [undefined, targetId];
  if (operation === "USER_SUSPEND") return [targetId, "suspend"];
  if (operation === "USER_RESTORE") return [targetId, "restore"];
  if (operation === "USER_BAN") return [targetId, "ban"];
  if (operation === "APPROVAL_ACTION_POLICY_UPDATE") {
    const [actionCode, roleCode] = targetId.split("~");
    return [actionCode, roleCode];
  }
  if (operation === "APPROVAL_APPROVE") return [targetId, "APPROVE"];
  if (operation === "APPROVAL_REJECT") return [targetId, "REJECT"];
  if (operation === "APPROVAL_REQUEST_CHANGES") return [targetId, "REQUEST_CHANGES"];
  if (operation === "PARTNER_IMPORT_CANCEL") return [targetId, "cancel"];
  if (operation === "PARTNER_IMPORT_RETRY") return [targetId, "retry"];
  return [targetId];
}

export function demoUpdateBinding(
  method: DemoWorkspaceMethod,
  args: unknown[],
): UpdateOutcomeBinding | undefined {
  if (method === "createApprovalRequest") return updateProposalBinding(args[0]);
  if (method === "saveLegalDraft" && args[1] === undefined) return undefined;
  if (method === "mutateAccess") {
    const operation =
      args[1] === "suspend"
        ? "USER_SUSPEND"
        : args[1] === "restore"
          ? "USER_RESTORE"
          : args[1] === "ban"
            ? "USER_BAN"
            : undefined;
    if (!operation) return unavailable();
    return { operation, targetId: target(args[0]), sourceOperation: operation };
  }
  if (method === "mutateAdministrator") {
    const operation =
      args[1] === "suspend" ? "USER_SUSPEND" : args[1] === "restore" ? "USER_RESTORE" : undefined;
    if (!operation) return unavailable();
    return {
      operation,
      targetId: target(args[0]),
      sourceOperation:
        operation === "USER_SUSPEND" ? "ADMINISTRATOR_SUSPEND" : "ADMINISTRATOR_RESTORE",
    };
  }
  if (method === "updateActionPolicy")
    return {
      operation: "APPROVAL_ACTION_POLICY_UPDATE",
      targetId: compositeTarget(`${String(args[0] ?? "")}~${String(args[1] ?? "")}`),
      sourceOperation: "APPROVAL_ACTION_POLICY_UPDATE",
    };
  if (method === "decideApprovalRequest") {
    const operation =
      args[1] === "APPROVE"
        ? "APPROVAL_APPROVE"
        : args[1] === "REJECT"
          ? "APPROVAL_REJECT"
          : args[1] === "REQUEST_CHANGES"
            ? "APPROVAL_REQUEST_CHANGES"
            : undefined;
    if (!operation) return unavailable();
    return { operation, targetId: target(args[0]), sourceOperation: operation };
  }
  if (method === "controlPartnerImport") {
    const operation =
      args[1] === "cancel"
        ? "PARTNER_IMPORT_CANCEL"
        : args[1] === "retry"
          ? "PARTNER_IMPORT_RETRY"
          : undefined;
    if (!operation) return unavailable();
    return { operation, targetId: target(args[0]), sourceOperation: operation };
  }
  const operation = existingOutcomeOperations.find(
    (value) => existingMutationDefinitions[value].demoMethod === method,
  );
  if (!operation) return undefined;
  if (operation === "PARTNER_IMPORT_PREVIEW" || operation === "PARTNER_SETUP_CREATE")
    return { operation, sourceOperation: operation };
  const targetId = target(args[operation === "LEGAL_DRAFT_UPDATE" ? 1 : 0]);
  if (operation !== "SCHEDULE_STATUS") return { operation, targetId, sourceOperation: operation };
  const action = args[1];
  if (action !== "cancel" && action !== "archive") return unavailable();
  return {
    operation,
    targetId,
    sourceOperation: action === "cancel" ? "SCHEDULE_CANCEL" : "SCHEDULE_ARCHIVE",
  };
}

/** Closed registration metadata for the synthetic owner, never a caller-selected method. */
export function outcomeRegistration(operation: OutcomeOperation) {
  if (!outcomeOperations.includes(operation)) return unavailable();
  for (const logical of [...createOutcomeOperations, ...existingOutcomeOperations]) {
    const proposal = operation === `${logical}_APPROVAL`;
    const direct =
      logical === "SCHEDULE_STATUS"
        ? operation === "SCHEDULE_CANCEL" || operation === "SCHEDULE_ARCHIVE"
        : logical === "USER_SUSPEND"
          ? operation === "USER_SUSPEND" || operation === "ADMINISTRATOR_SUSPEND"
          : logical === "USER_RESTORE"
            ? operation === "USER_RESTORE" || operation === "ADMINISTRATOR_RESTORE"
            : operation === logical;
    if (!proposal && !direct) continue;
    const existing = Object.hasOwn(existingMutationDefinitions, logical);
    return {
      operation: logical,
      proposal,
      targetRequired:
        existing &&
        Boolean(existingMutationDefinitions[logical as ExistingOutcomeOperation].targetField),
      resultMayDifferFromTarget:
        !proposal &&
        [
          "APPROVAL_REVISE",
          "SCHEDULE_SERIES_UPDATE",
          "PARTNER_IMPORT_REGENERATE",
          "PARTNER_SETUP_SAVE",
          "PARTNER_IMPORT_DECISION",
          "PARTNER_IMPORT_PURGE_RETRY",
        ].includes(operation),
      definition: existing
        ? existingMutationDefinitions[logical as ExistingOutcomeOperation]
        : createOutcomeDefinitions[logical as CreateOutcomeOperation],
      directOperation: logical === "SCHEDULE_STATUS" ? ("SCHEDULE_CANCEL" as const) : logical,
    };
  }
  return unavailable();
}
